/** @jsx jsx */
import {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {jsx, css} from '@emotion/react'

import {isAndroid, isIOS, isIpad} from '../../../util/userAgent'
import {EVENTS, logDplWithAppParams} from '../../../util/dpl'
import {getCountry} from '../../../util/getCountry'
import {colors} from '../../../constants'
import {iOSAppStoreLink, androidAppStoreLink} from '../../../util/appDownloadLinks'
import AppleIcon from '../../../images/app/AppleIcon.svg'
import AndroidIcon from '../../../images/app/AndroidIcon.svg'

const translations = {
    en: {
        iphoneText: `Download for iPhone`,
        androidText: `Download for Android`,
        joinTheWaitlist: `Join the waitlist`,
    },
    de: {
        iphoneText: `Herunterladen für iPhone`,
        androidText: `Herunterladen für Android`,
        joinTheWaitlist: `Auf die Warteliste setzen`,
    },
    nl: {
        iphoneText: `Downloaden voor iPhone`,
        androidText: `Downloaden voor Android`,
        joinTheWaitlist: `Meld je aan voor de wachtlijst`,
    },
}

const buttonStylesDefault = css`
    font-family: 'Inter', sans-serif;
    border-radius: 10px;
    display: inline-block;
    font-family: inherit;
    letter-spacing: 0.02rem;
    line-height: 1.2;
    transition: background-color 0.3s cubic-bezier(0, 0.4, 0.74, 0.63);
    background-color: ${colors.teal};
    border: none;
    color: ${colors.black};
    padding: 24px;
    font-weight: 800;
    box-shadow: 0px 20px 26px 0px rgba(0, 0, 0, 0.15);
    .button-inner {
        display: flex;
        align-items: center;
        .de-text {
            font-size: 18px;
        }
        .nl-text {
            font-size: 18px;
        }
        .en-text {
            font-size: 22px;
        }
        @media (max-width: 350px) {
            .de-text {
                font-size: 16px;
            }
            .nl-text {
                font-size: 16px;
            }
            .en-text {
                font-size: 18px;
            }
        }
    }
    span {
        color: ${colors.black};
    }
    .logo {
        margin-right: 10px;
    }
    &:hover {
        cursor: pointer;
        background-color: ${colors.tealLight2};
    }
`

const Button = ({
    text,
    experiment,
    onClick,
    ctaType,
    ariaLabel,
    tabIndex = '0',
    languageCode = 'en',
    overrideStyles,
    os,
}) => {
    const [ctaLink, setCtaLink] = useState('')
    const [ctaText, setCtaText] = useState('')

    const txt = translations[languageCode]

    useEffect(() => {
        setCtaText(txt.joinTheWaitlist)
        if (isIOS() || isIpad()) {
            setCtaText(txt.iphoneText)
            setCtaLink(iOSAppStoreLink)
        } else if (isAndroid()) {
            setCtaText(txt.androidText)
            setCtaLink(androidAppStoreLink)
        } else {
            setCtaText(txt.joinTheWaitlist)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const onBtnClickDownload = () => {
        logDplWithAppParams(EVENTS.StartpageAppCtaClick, {
            os,
            country: getCountry(),
            type: ctaType,
            experiment,
        }).then(() => {
            const event = new CustomEvent('showSuccessModal', {detail: {ctaType}})
            window.dispatchEvent(event)
        })
    }

    const onBtnClickWaitlist = (event) => {
        event.preventDefault()
        logDplWithAppParams(EVENTS.StartpageAppCtaClick, {
            os,
            country: getCountry(),
            type: ctaType,
            experiment,
        })
        const newsletterSection = document.querySelector('#waitlist-section')
        if (newsletterSection) {
            window.scrollTo({
                // @ts-ignore
                top: newsletterSection.offsetTop,
                behavior: 'smooth',
            })
        }
    }

    return (
        <a
            href={ctaLink}
            download
            className="conversion-button"
            // leaving the code below intentionally. we will wanna switch to this eventually until android is done
            onClick={onClick || onBtnClickDownload}
            aria-label={ariaLabel || text || ctaText}
            css={css`
                ${buttonStylesDefault}
                ${overrideStyles}
            `}
            tabIndex={tabIndex}
            type="button"
        >
            <div
                className="button-inner"
                css={css`
                    transition: opacity 0.2s ease;
                    display: flex;
                    align-items: center;
                    svg {
                        margin-top: ${os === 'android' ? '0' : '-2px'};
                        margin-right: 10px;
                    }
                `}
            >
                {os === 'android' ? <AndroidIcon /> : <AppleIcon />}
                <span
                    className={`text ${languageCode}-text`}
                    css={css`
                        text-align: left;
                    `}
                >
                    {text || ctaText}
                </span>
            </div>
        </a>
    )
}

Button.propTypes = {
    experiment: PropTypes.string,
    onClick: PropTypes.func,
    ctaType: PropTypes.string.isRequired,
    text: PropTypes.string,
    ariaLabel: PropTypes.string,
    overrideStyles: PropTypes.string,
}

Button.defaultProps = {
    experiment: `control`,
    overrideStyles: ``,
}

export default Button
